import type { ReactNode } from 'react';
import type { MetricCardTrend } from '@/components/MetricCard';
import { Fragment } from 'react';
import { cn } from 'helpers/cn';

/**
 * The console building blocks the dashboard screens are assembled from.
 *
 * They live beside the Home screen rather than in `src/workspaces/components`
 * because that package is shared with the editor app, which is still on the old
 * card language — moving these there would restyle a second product by accident.
 * Health and Performance import them from here for the same reason `LogPanel`
 * imports `attention-descriptors` from here: one definition beats three copies.
 *
 * Nothing in this file picks a colour. Every surface, line and status tone is a
 * token, so both themes follow from `styles/globals.css` alone.
 */

/**
 * The focus ring every control in this file has to compose in by hand.
 *
 * shadcn's `Button` carries one; these are plain `<button>`s, so dressing a
 * control in the console language silently dropped the only thing that tells a
 * keyboard user where they are. The colour is `--ring` in both themes, and
 * `outline-none` only ever ships alongside a replacement — never on its own.
 */
export const FOCUS_RING = 'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none';

type PanelProps = {
  title: string;
  description?: string;
  /** Controls on the header's right edge: pills, counts, toggles. */
  actions?: ReactNode;
  /**
   * A closing strip, in the same `panel2` as the header.
   *
   * Where a form's submit belongs: a save button in the header sits above the fields
   * it commits, which reads as a heading rather than as the end of a task. The design
   * has always specified both strips; only the header existed.
   */
  footer?: ReactNode;
  className?: string;
  bodyClassName?: string;
  /** Names the panel for the product tour, as its `data-tour` anchor. */
  tourAnchor?: string;
  children?: ReactNode;
};

/**
 * A section panel. A page is a vertical stack of these, and nothing else.
 *
 * The head strip is `panel2` rather than `panel`: inside the workspace panel a
 * section would otherwise be a rectangle of the same colour, readable only by
 * its hairline.
 */
export const Panel = ({
  title,
  description,
  actions,
  footer,
  className,
  bodyClassName,
  tourAnchor,
  children,
}: PanelProps) => (
  <section
    className={cn('bg-panel border-line rounded-panel overflow-hidden border', className)}
    data-tour={tourAnchor}
  >
    {/* Below `md` the title and its controls are stacked rows, and the controls get
        the full width: at 375px a title plus a search field or a pill strip on one
        line either collides or pushes the panel into a horizontal scroll. */}
    <div
      className={cn(
        'bg-panel2 border-line flex flex-col gap-2 border-b px-3.5 py-2.5',
        'md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3',
      )}
    >
      <div className="min-w-0">
        <h2 className="text-foreground m-0 text-body font-medium leading-tight">{title}</h2>
        {!!description && <p className="supporting m-0">{description}</p>}
      </div>
      {!!actions && <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:shrink-0">{actions}</div>}
    </div>
    {!!children && <div className={cn('p-3.5', bodyClassName)}>{children}</div>}
    {!!footer && (
      <div className="bg-panel2 border-line flex flex-col-reverse gap-2 border-t px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-end">
        {footer}
      </div>
    )}
  </section>
);

type FieldProps = {
  label: string;
  htmlFor?: string;
  /** One line under the control. For a consequence, not a restatement of the label. */
  hint?: string;
  children: ReactNode;
};

/**
 * A labelled control. The label is a mono micro-label like every other label in the
 * console, and the hint is the only place a field gets to explain itself.
 */
export const Field = ({ label, htmlFor, hint, children }: FieldProps) => (
  <div className="grid gap-1.5">
    <label htmlFor={htmlFor} className="micro-label">
      {label}
    </label>
    {children}
    {!!hint && <span className="supporting">{hint}</span>}
  </div>
);

/** The console's field control: 36px, field fill, visible outline. */
export const FIELD_CONTROL = 'border-input bg-field text-foreground h-9 rounded-lg text-body';

type FactProps = { label: string; value: ReactNode };

/**
 * A read-only fact. Facts and fields look different on purpose - an account id is
 * not a blank someone forgot to fill in.
 */
export const Fact = ({ label, value }: FactProps) => (
  <div className="grid gap-1">
    <span className="micro-label">{label}</span>
    <span className="text-foreground font-mono text-body">{value}</span>
  </div>
);

type MetricGridProps = { children: ReactNode };

/** Tiles wrap on their own rather than at a breakpoint, so a panel can be any width. */
export const MetricGrid = ({ children }: MetricGridProps) => (
  <div className="grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(180px,1fr))]">{children}</div>
);

type MetricTileProps = {
  label: string;
  value: string;
  /** Token class for the number. The colour is the meaning; the default is neutral. */
  valueClassName?: string;
  description?: string;
  subtitle?: string;
  /** Sits opposite the label — a trend chip, a share percentage. */
  trailing?: ReactNode;
  /** Rendered under the description. A meter, usually. */
  children?: ReactNode;
  /**
   * Makes the tile a filter.
   *
   * A count is a question - "seven need a decision, which seven?" - and a tile that
   * cannot answer it makes the reader go looking. Passing this turns the whole tile
   * into a toggle; `isActive` draws it as the one in force.
   */
  onClick?: () => void;
  isActive?: boolean;
};

export const MetricTile = (props: MetricTileProps) => {
  const { label, value, valueClassName, description, subtitle, trailing, children, onClick, isActive } = props;

  const body = (
    <>
      <div className="flex items-center justify-between gap-2">
        <span className="micro-label truncate">{label}</span>
        {trailing}
      </div>
      <span className={cn('metric-value', valueClassName ?? 'text-foreground')}>{value}</span>
      {!!description && <span className="supporting">{description}</span>}
      {children}
      {!!subtitle && <span className="micro-label">{subtitle}</span>}
    </>
  );

  if (!onClick) {
    return <div className="border-line grid content-start gap-[7px] rounded-card border p-[13px]">{body}</div>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        'grid content-start gap-[7px] rounded-card border p-[13px] text-left transition-colors duration-150',
        FOCUS_RING,
        isActive ? 'border-sel-line bg-sel-bg' : 'border-line hover:bg-hover',
      )}
    >
      {body}
    </button>
  );
};

type MeterProps = {
  percent: number;
  /** Fill colour, e.g. `bg-ok`. The track is always `bg-track`. */
  className: string;
};

export const Meter = ({ percent, className }: MeterProps) => (
  <span className="bg-track block h-[3px] w-full overflow-hidden rounded-pill">
    <span
      className={cn('block h-full rounded-pill', className)}
      style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
    />
  </span>
);

type TrendChipProps = { trend: MetricCardTrend };

/**
 * Same wording as the card it replaces: signed, one decimal, no icon needed.
 *
 * Solid, unlike a status chip. The distinction is that here the fill IS the
 * encoding - up or down is the whole message, and it has to be legible at a
 * glance across a row of tiles - whereas a status chip already says "running" or
 * "draft" in words beside its dot. The solid tokens are a separate pair from
 * --ok/--danger for a reason: those are tuned as text on a panel, and white on
 * the light --ok is 3.74:1, which fails AA at this size.
 */
export const TrendChip = ({ trend }: TrendChipProps) => (
  <span
    className={cn(
      'shrink-0 rounded-pill px-1.5 py-px font-mono text-nano font-medium leading-[1.6]',
      trend.direction === 'up' ? 'bg-ok-solid text-ok-solid-fg' : 'bg-danger-solid text-danger-solid-fg',
    )}
  >
    {trend.percent > 0 ? '+' : ''}
    {trend.percent.toFixed(1)}%
  </span>
);

export type PillOption = { label: string; value: string };

type PillTabsProps = {
  options: readonly PillOption[];
  value: string;
  onChange: (value: string) => void;
};

/**
 * Buttons with `aria-pressed`, not a tablist: none of these control a tab panel,
 * they re-scope the page under them.
 */
export const PillTabs = ({ options, value, onChange }: PillTabsProps) => (
  // Below `md` the strip is one swipeable line rather than a wrapping block: a
  // filter strip that wraps to three rows at 375px pushes the content it filters
  // off the screen, and the reader loses the thing they came to look at.
  <div className="flex w-full items-center gap-1.5 overflow-x-auto md:w-auto md:flex-wrap md:overflow-visible">
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        aria-pressed={option.value === value}
        onClick={() => onChange(option.value)}
        className={cn(
          // min-h-7: a 11px pill with py-1 is a 20px target, well under the 28px
          // a thumb can reliably hit. min-h-9 on mobile clears the 36px floor.
          'flex min-h-9 shrink-0 items-center rounded-pill border px-2.5 py-1 font-mono text-micro leading-none md:min-h-7',
          'transition-colors duration-150',
          FOCUS_RING,
          option.value === value
            ? 'border-sel-line bg-sel-bg text-foreground'
            : // border-input, not border-line: an unselected pill has no fill, so the
              // outline is the only thing drawing it, and at 10% alpha it was not there.
              'border-input text-dim hover:bg-hover hover:text-foreground',
        )}
      >
        {option.label}
      </button>
    ))}
  </div>
);

type ScopePillsProps = {
  value: string;
  onChange: (scope: string) => void;
  /** Agencies pick between accounts; a single-account customer picks websites. */
  isAgency?: boolean;
};

/**
 * The scope selector shared by Health and Performance, wearing the console's
 * pills. Word for word the three choices the old `ScopeTabs` offered — the two
 * pages sit next to each other in the Monitors menu and must not disagree.
 */
export const ScopePills = ({ value, onChange, isAgency = false }: ScopePillsProps) => (
  <PillTabs
    value={value}
    onChange={onChange}
    options={[
      { label: isAgency ? 'All accounts' : 'All websites', value: 'all' },
      { label: 'Account specific', value: 'account' },
      { label: 'Only mine', value: 'mine' },
    ]}
  />
);

/**
 * `micro-label`'s shape without its colour.
 *
 * The utility hard-sets `color: dim`, and utility ordering makes overriding it
 * with a `text-*` class a coin toss. Anything that has to carry a tone — a
 * status, a count — composes this instead and names its own colour.
 */
export const MICRO_LABEL_SHAPE = 'font-mono text-nano font-medium uppercase tracking-[0.1em]';

/**
 * A table row's own button — "Implementation", "Event Goals", "Select".
 *
 * Mono caps, because it labels a row rather than heading a page, but kept small:
 * at wide tracking and a 32px box these read as page-level calls to action and
 * shouted over the data they belong to. 28px, tighter tracking, and the same
 * `text-nano` as the chips beside them, so a row's controls sit at the row's
 * scale. Mobile is unaffected: `DataTableCards` floors every button in a card at
 * `min-h-9`, and min-height beats height.
 *
 * Spelled out rather than composing `micro-label` on purpose — that utility hard-sets
 * letter-spacing, and `tailwind-merge` cannot dedupe a custom `@utility` against
 * `tracking-*`, so the two would race on CSS source order.
 */
export const ROW_ACTION_BUTTON =
  'border-line text-dim hover:text-foreground hover:bg-hover h-7 rounded-lg bg-transparent px-2 font-mono text-nano font-medium uppercase tracking-[0.08em]';

/**
 * Console dress for the shared `SearchInput`.
 *
 * The component lives in `src/workspaces/components`, which the editor app also
 * ships, so it cannot be restyled in place. It does forward a class onto its
 * wrapper, and the field is the only element under it — reaching that far and no
 * further keeps the mobile expand-and-focus behaviour exactly as it was.
 */
export const SEARCH_FIELD_CLASS =
  '[&_input]:border-line [&_input]:bg-field [&_input]:h-8 [&_input]:rounded-lg [&_input]:text-body';

/** Every screen here reports the same three severities, so they share one scale. */
export type Severity = 'broken' | 'decide' | 'queue';

export const SEVERITY_DOT: Record<Severity, string> = {
  broken: 'bg-danger',
  decide: 'bg-warn',
  queue: 'bg-bar-muted',
};

export const SEVERITY_TEXT: Record<Severity, string> = {
  broken: 'text-danger',
  decide: 'text-warn',
  queue: 'text-dim',
};

export const SEVERITY_BADGE: Record<Severity, string> = {
  broken: 'border-danger-line text-danger',
  decide: 'border-warn-line text-warn',
  queue: 'border-line text-dim',
};

/**
 * The dot is the only thing on a row that carries severity, so it cannot be
 * `aria-hidden` and it cannot be colour alone: a 6px teal/amber/grey difference is
 * exactly what a red-green reader or a greyscale screenshot loses. The word is
 * hidden visually and read out, and hovering names it.
 */
export const SEVERITY_LABEL: Record<Severity, string> = {
  broken: 'Broken',
  decide: 'Needs a decision',
  queue: 'Waiting to be built',
};

type SeverityDotProps = { severity: Severity };

export const SeverityDot = ({ severity }: SeverityDotProps) => (
  <span className={cn('size-1.5 shrink-0 rounded-pill', SEVERITY_DOT[severity])} title={SEVERITY_LABEL[severity]}>
    <span className="sr-only">{SEVERITY_LABEL[severity]}</span>
  </span>
);

type CountBadgeProps = { severity: Severity; children: ReactNode };

export const CountBadge = ({ severity, children }: CountBadgeProps) => (
  <span
    className={cn(
      'rounded-pill border px-2 py-0.5 font-mono text-nano uppercase leading-[1.6] tracking-[0.08em]',
      SEVERITY_BADGE[severity],
    )}
  >
    {children}
  </span>
);

type EmptyStateProps = { children: ReactNode };

/** One sentence. An empty panel says why it is empty and nothing more. */
export const EmptyState = ({ children }: EmptyStateProps) => <p className="supporting m-0">{children}</p>;

type ActionButtonProps = {
  onClick: () => void;
  children: ReactNode;
  className?: string;
  /** Spelled-out purpose, for when the visible verb alone is ambiguous out of context. */
  title?: string;
  /**
   * The accessible name, when the visible verb repeats down a column ("Disable",
   * "Extend trial"): a screen reader hears only the verb, not which row it is on.
   */
  ariaLabel?: string;
  disabled?: boolean;
};

/** The verb at the end of a row. Small, quiet, and the only clickable thing there. */
export const ActionButton = ({ onClick, children, className, title, ariaLabel, disabled }: ActionButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    aria-label={ariaLabel}
    disabled={disabled}
    className={cn(
      // border-input, not border-line: this carries no fill, so the outline is the
      // only thing saying it is a control rather than a word at the end of a row.
      'border-input text-dim hover:bg-hover hover:text-foreground flex shrink-0 items-center gap-1 rounded-lg',
      // min-h-7: 10px mono with py-1 is a 22px target. This is the only control on
      // its row, so it is the one thing that has to be hittable. On a phone it takes
      // the 36px floor instead — a row action is the whole point of the row there.
      'border px-2 py-1 font-mono text-nano uppercase tracking-[0.1em] transition-colors duration-150',
      'min-h-9 md:min-h-7',
      'disabled:cursor-not-allowed disabled:opacity-40',
      FOCUS_RING,
      className,
    )}
  >
    {children}
  </button>
);

type DisclosureButtonProps = {
  onClick: () => void;
  children: ReactNode;
};

/** "Show 4 more" and friends. Never a primary action, so never a button shape. */
export const DisclosureButton = ({ onClick, children }: DisclosureButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'text-dim hover:text-foreground self-start rounded-sm font-mono text-micro underline underline-offset-2',
      'transition-colors duration-150',
      FOCUS_RING,
    )}
  >
    {children}
  </button>
);

type TableScrollProps = {
  /** Sum of the fixed columns plus the text column's minimum. Below it, scroll. */
  minWidth: number;
  children: ReactNode;
};

/**
 * Columns scroll rather than collapse, so a number never wraps onto two lines.
 *
 * Below `md` this is still a sideways scroll, which is the right trade for a table
 * whose identity column stays on screen at 375px. Where it does not — the statistics
 * tables, where the reader has to swipe past the variant name to reach the interval —
 * use `StackedRow` for the narrow shape instead.
 */
export const TableScroll = ({ minWidth, children }: TableScrollProps) => (
  <div className="overflow-x-auto">
    <div style={{ minWidth: `${minWidth}px` }}>{children}</div>
  </div>
);

type GridTableProps = {
  /** Names the table for a screen reader, which has no head row to read from. */
  label: string;
  /** Sum of the fixed columns plus the flexible column's minimum. Below it, scroll. */
  minWidth: number;
  /** `grid-template-columns` for the whole table. Declared here and nowhere else. */
  template: string;
  children: ReactNode;
};

/**
 * A grid that behaves like a table: one set of column tracks, shared by the head row
 * and every body row.
 *
 * The columns are declared once — here, on the table — and re-used by the rows with
 * `grid-cols-subgrid`. Giving each row its own `grid-template-columns` instead looks
 * equivalent and is not, which is what made the statistics tables crooked: a
 * `minmax(x, auto)` track is resolved against the content of the grid it is declared
 * on, so the head row sized its columns to the words "z-score" and "p-value" while
 * each body row sized the same columns to the numbers underneath. On a wide screen the
 * two disagreed by 121px and every heading sat to the right of the figures it labelled.
 * It looked correct at narrow widths, where there is no spare space for them to
 * disagree about and every track sits on its floor — which is why it survived review.
 *
 * The horizontal padding is the table's rather than the row's, because a row's own
 * padding is subtracted from the first and last track: with `px-4` on the rows a 72px
 * ID column had 54px to draw in. Rows put themselves back over that padding with
 * `-mx-4`, so a hover still reaches the card's edges while the cells stay in their
 * columns. The 2px left border is the one inset still taken off the first track, and
 * every row carries it whether or not it is drawn, so none of them shifts out of line.
 */
export const GridTable = ({ label, minWidth, template, children }: GridTableProps) => (
  <div className="hidden overflow-x-auto md:block">
    <div
      role="table"
      aria-label={label}
      className="grid gap-x-4 px-4"
      style={{ minWidth: `${minWidth}px`, gridTemplateColumns: template }}
    >
      {children}
    </div>
  </div>
);

/**
 * Every row, head included. Re-uses the table's tracks rather than resolving its own,
 * and reaches back out over the table's padding so its background is full width.
 */
export const GRID_TABLE_ROW = 'col-span-full -mx-4 grid grid-cols-subgrid px-4';

/**
 * The `rowgroup` between the table and its rows. It has to re-use the tracks too —
 * a subgrid inherits from its parent, and a plain wrapper in between would leave the
 * rows resolving their own columns again.
 */
export const GRID_TABLE_ROWGROUP = 'col-span-full grid grid-cols-subgrid';

export type StackedField = {
  /** Stable key. The label is a node, so it cannot serve as one. */
  key: string;
  /** The column head, repeated as the label on a phone. */
  label: ReactNode;
  value: ReactNode;
  /** The tone the desktop cell carries, so both shapes say the same thing. */
  className?: string;
};

type StackedRowProps = {
  /** The row's identity: its name, and any chip that qualifies it. */
  heading: ReactNode;
  fields: readonly StackedField[];
  /** The leading-row rule, so the conclusion survives the narrow layout. */
  className?: string;
  /** The row's controls, which have no column to sit in once the grid is gone. */
  children?: ReactNode;
};

/**
 * One table row, restacked as label/value pairs for a viewport too narrow to scroll.
 *
 * A sideways scroll is fine while the identity column is still visible. On the
 * statistics tables at 375px it is not: the grid's minimum is 900-1140px, so
 * swiping across to the confidence interval leaves the reader holding a number with
 * nothing attached to it, and comparing a variant against the control — the whole
 * reason the page exists — stops being possible. Stacking repeats the column head
 * beside every value instead, which costs vertical space and loses nothing.
 */
export const StackedRow = ({ heading, fields, className, children }: StackedRowProps) => (
  <div className={cn('border-line rounded-card border border-l-2 border-l-transparent p-3', className)}>
    <div className="mb-2 min-w-0">{heading}</div>
    <dl className="m-0 grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5">
      {fields.map((field) => (
        <Fragment key={field.key}>
          <dt className="micro-label self-center">{field.label}</dt>
          <dd className={cn('num text-foreground m-0 text-right', field.className)}>{field.value}</dd>
        </Fragment>
      ))}
    </dl>
    {children}
  </div>
);

type PagerProps = {
  currentPage: number;
  totalPages: number;
  onChange: (page: number) => void;
};

export const Pager = ({ currentPage, totalPages, onChange }: PagerProps) => (
  <div className="border-line bg-panel2 flex items-center justify-between gap-3 border-t px-3.5 py-2">
    <span className="micro-label">
      Page {currentPage} of {totalPages}
    </span>
    <div className="flex items-center gap-1">
      <PagerButton label="First page" onClick={() => onChange(1)} disabled={currentPage === 1}>
        «
      </PagerButton>
      <PagerButton
        label="Previous page"
        onClick={() => onChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
      >
        ‹
      </PagerButton>
      <PagerButton
        label="Next page"
        onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
      >
        ›
      </PagerButton>
      <PagerButton label="Last page" onClick={() => onChange(totalPages)} disabled={currentPage === totalPages}>
        »
      </PagerButton>
    </div>
  </div>
);

type PagerButtonProps = {
  label: string;
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
};

const PagerButton = ({ label, onClick, disabled, children }: PagerButtonProps) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    disabled={disabled}
    // size-7, not size-6: a 24px arrow is under the 28px a thumb can hit, and
    // paging is a repeated action — the one place a miss costs the most. size-9 on a
    // phone, where a thumb rather than a cursor is doing the aiming.
    className={cn(
      'border-input text-dim hover:bg-hover hover:text-foreground flex size-9 items-center justify-center rounded-lg md:size-7',
      'border font-mono text-micro transition-colors duration-150',
      'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
      FOCUS_RING,
    )}
  >
    {children}
  </button>
);
